import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { apiUrl } from '../../utils/apiBase';

const getBackendAuthHeaders = async () => {
  const headers = {};
  const sharedSecret = String(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET || '').trim();
  if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
  try {
    const token = await auth.currentUser?.getIdToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    // Backend, token yoksa mevcut güvenlik ayarına göre isteği reddeder.
  }
  return headers;
};

const parseConfigBoolean = (value, fallback = false) => {
  if (value === true || value === false) return value;
  const raw = String(value ?? '')
    .trim()
    .toLowerCase();
  if (raw === 'true' || raw === '1' || raw === 'yes') return true;
  if (raw === 'false' || raw === '0' || raw === 'no') return false;
  return fallback;
};

const emptyResult = {
  ok: null,
  status: null,
  message: '',
  payload: null
};

const readJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const summarizeAuthPayload = (json) => {
  const result = json?.result || {};
  return {
    hasUserAgent: result.hasUserAgent ?? result.userAgentConfigured ?? false,
    authConfigured: Boolean(result.hasApiKey && result.hasApiSecret && result.hasSupplierId),
    supplierIdSource: result.supplierIdSource || '-',
    message: result.message || json?.message || ''
  };
};

const ResultCard = ({ title, result, type }) => {
  const payload = result?.payload || {};
  const authSummary = type === 'auth' ? summarizeAuthPayload(payload) : null;
  const categoryCount = type === 'category' && Array.isArray(payload.categories) ? payload.categories.length : null;
  const severity = result.ok === true ? 'success' : result.ok === false ? 'error' : 'info';

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#fff' }}>
      <CardContent>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
            {result.status ? <Chip size="small" label={`HTTP ${result.status}`} /> : null}
            {result.ok !== null ? (
              <Chip
                size="small"
                color={result.ok ? 'success' : 'error'}
                label={result.ok ? 'Başarılı' : 'Hata'}
              />
            ) : (
              <Chip size="small" label="Bekliyor" />
            )}
          </Stack>

          {result.message ? (
            <Alert severity={severity} sx={{ py: 0.5 }}>
              {result.message}
            </Alert>
          ) : null}

          {authSummary ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
              <Chip label={`User-Agent: ${authSummary.hasUserAgent ? 'var' : 'yok'}`} color={authSummary.hasUserAgent ? 'success' : 'warning'} />
              <Chip label={`Auth: ${authSummary.authConfigured ? 'hazır' : 'eksik'}`} color={authSummary.authConfigured ? 'success' : 'warning'} />
              <Chip label={`Supplier: ${authSummary.supplierIdSource}`} />
            </Box>
          ) : null}

          {categoryCount !== null ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
              <Chip label={`Kategori sayısı: ${categoryCount}`} color={categoryCount > 0 ? 'success' : 'warning'} />
              <Chip label="Arama: bilezik" />
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

function TrendyolConnectionTestPanel() {
  const [authResult, setAuthResult] = useState(emptyResult);
  const [categoryResult, setCategoryResult] = useState(emptyResult);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(false);

  const [firebaseUserSignedIn, setFirebaseUserSignedIn] = useState(false);
  const [liveSaveEnabled, setLiveSaveEnabled] = useState(false);
  const [liveSaveLoading, setLiveSaveLoading] = useState(false);
  const [liveSaveError, setLiveSaveError] = useState('');

  const loadTrendyolLiveFlag = useCallback(async () => {
    setLiveSaveError('');
    try {
      const response = await fetch(apiUrl('/api/trendyol/config'), {
        method: 'GET',
        headers: await getBackendAuthHeaders()
      });
      const json = await readJsonSafely(response);
      if (!response.ok || json?.success !== true) {
        throw new Error(json?.message || json?.error || 'Trendyol ayarları okunamadı');
      }
      const cfg = json?.config && typeof json.config === 'object' ? json.config : {};
      setLiveSaveEnabled(parseConfigBoolean(cfg.liveSaveEnabled, false));
    } catch (e) {
      setLiveSaveError(e?.message || String(e));
      setLiveSaveEnabled(false);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      const signedIn = Boolean(user);
      setFirebaseUserSignedIn(signedIn);
      if (signedIn) {
        await loadTrendyolLiveFlag();
      } else {
        setLiveSaveEnabled(false);
        setLiveSaveError('');
      }
    });
    return () => unsub();
  }, [loadTrendyolLiveFlag]);

  const runGetTest = async ({ path, setResult, setLoading }) => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl(path), {
        method: 'GET',
        headers: await getBackendAuthHeaders()
      });
      const json = await readJsonSafely(response);
      setResult({
        ok: response.ok && json?.success !== false,
        status: response.status,
        message: json?.message || json?.error || '',
        payload: json
      });
    } catch (error) {
      setResult({
        ok: false,
        status: null,
        message: error?.message || String(error),
        payload: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLiveSaveToggle = async (_e, checked) => {
    setLiveSaveError('');
    setLiveSaveLoading(true);
    try {
      const response = await fetch(apiUrl('/api/trendyol/config'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getBackendAuthHeaders())
        },
        body: JSON.stringify({ liveSaveEnabled: checked })
      });
      const json = await readJsonSafely(response);
      if (!response.ok || json?.success === false) {
        throw new Error(json?.message || json?.error || 'Kayıt başarısız');
      }
      setLiveSaveEnabled(checked);
    } catch (e) {
      setLiveSaveError(e?.message || String(e));
      await loadTrendyolLiveFlag();
    } finally {
      setLiveSaveLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        minHeight: '100%',
        bgcolor: '#f8fafc'
      }}
    >
      <Stack spacing={2.25} sx={{ maxWidth: 980, mx: 'auto' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a', mb: 0.5 }}>
            Trendyol Bağlantı Testi
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Bu alan yalnızca giriş yapılmış kullanıcıyla backend bağlantısını test eder. Ürün gönderimi,
            batch veya kategori eşleştirme işlemi yapmaz.
          </Typography>
        </Box>

        {firebaseUserSignedIn ? (
          <Card sx={{ borderRadius: 3, boxShadow: '0 8px 26px rgba(15, 23, 42, 0.06)', border: '1px solid #e2e8f0' }}>
            <CardContent>
              <Typography sx={{ fontWeight: 900, color: '#c2410c', mb: 1 }}>Canlı gönderim ayarı</Typography>
              <FormControlLabel
                control={
                  <Switch
                    color="warning"
                    checked={liveSaveEnabled}
                    disabled={liveSaveLoading}
                    onChange={handleLiveSaveToggle}
                  />
                }
                label={<Typography sx={{ fontWeight: 800 }}>Canlı Trendyol gönderimi aktif</Typography>}
              />
              <FormHelperText sx={{ ml: 0, maxWidth: 640 }}>
                Açılırsa Trendyol’a Gönder butonu gerçek ürün oluşturma isteği gönderir.
              </FormHelperText>
              {liveSaveError ? (
                <Alert severity="error" sx={{ mt: 1.25, borderRadius: 2 }}>
                  {liveSaveError}
                </Alert>
              ) : null}
              {liveSaveEnabled ? (
                <Alert severity="warning" sx={{ mt: 1.25, borderRadius: 2 }}>
                  Canlı mod açık — test ürünleri Trendyol kuyruğuna gidebilir. Bitince kapatmayı unutmayın.
                </Alert>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        <Card sx={{ borderRadius: 3, boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
                <Button
                  variant="contained"
                  disabled={loadingAuth}
                  onClick={() =>
                    runGetTest({
                      path: '/api/trendyol/auth-test',
                      setResult: setAuthResult,
                      setLoading: setLoadingAuth
                    })
                  }
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  {loadingAuth ? <CircularProgress size={18} sx={{ mr: 1, color: 'inherit' }} /> : null}
                  Bağlantıyı Test Et
                </Button>
                <Button
                  variant="outlined"
                  disabled={loadingCategory}
                  onClick={() =>
                    runGetTest({
                      path: '/api/trendyol/categories?name=bilezik',
                      setResult: setCategoryResult,
                      setLoading: setLoadingCategory
                    })
                  }
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  {loadingCategory ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                  Kategori Testi Yap (bilezik)
                </Button>
              </Stack>

              <Divider />

              <Stack spacing={1.5}>
                <ResultCard title="Auth Test Sonucu" result={authResult} type="auth" />
                <ResultCard title="Kategori Test Sonucu" result={categoryResult} type="category" />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default TrendyolConnectionTestPanel;
